import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * @type {string[]}
   */
  #panelStack = ['root'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#activatePanel('root');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    this.#preloadImages();
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Opening the drawer signals intent to navigate, so lazy images across every panel (including
   * ones the visitor hasn't drilled into yet) can start loading immediately instead of waiting
   * for each one to individually near the viewport — mirrors header-menu.js's same preload
   * pattern for the desktop mega menu, so panel navigation doesn't have a loading lag deeper in
   * the tree.
   */
  #preloadImages() {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  }

  /**
   * Open the next drawer navigation panel.
   * @param {PointerEvent} event
   */
  openPanel(event) {
    event.preventDefault();

    const trigger = event.target instanceof Element ? event.target.closest('[data-menu-target]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    const targetId = trigger.dataset.menuTarget;
    if (!targetId || !this.#getPanel(targetId)) return;

    const panel = this.#getPanel(targetId);
    const level = panel ? Number(panel.dataset.level ?? 0) : 0;

    this.#panelStack = this.#panelStack.slice(0, level);
    this.#panelStack.push(targetId);
    this.#activatePanel(targetId);
  }

  /**
   * Return to the previous drawer navigation panel.
   * @param {PointerEvent} event
   */
  backPanel(event) {
    event.preventDefault();

    if (this.#panelStack.length <= 1) return;

    this.#panelStack.pop();
    this.#activatePanel(this.#panelStack[this.#panelStack.length - 1]);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Reset the Cartier-style panel menu to the first level.
   */
  #resetPanels() {
    this.#panelStack = ['root'];
    this.#activatePanel('root');
  }

  /**
   * Get a drawer navigation panel by id.
   * @param {string} panelId
   * @returns {HTMLElement | null}
   */
  #getPanel(panelId) {
    const panels = this.querySelectorAll('[data-menu-panel]');

    for (const panel of panels) {
      if (panel instanceof HTMLElement && panel.dataset.panelId === panelId) return panel;
    }

    return null;
  }

  /**
   * Activate a drawer navigation panel and keep its ancestors visible for horizontal transitions.
   * @param {string} panelId
   */
  #activatePanel(panelId) {
    const panel = this.#getPanel(panelId);
    const shell = this.querySelector('[data-drawer-panel-shell]');
    const panels = this.querySelectorAll('[data-menu-panel]');

    if (!panel || !(shell instanceof HTMLElement)) return;

    shell.style.setProperty('--active-level', panel.dataset.level ?? '0');

    panels.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const isActive = item === panel;
      const isVisible = this.#panelStack.includes(item.dataset.panelId ?? '');

      item.classList.toggle('is-active', isActive);
      item.classList.toggle('is-visible', isVisible);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.querySelectorAll('[data-menu-target]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const isExpanded = this.#panelStack.includes(item.dataset.menuTarget ?? '');
      item.setAttribute('aria-expanded', isExpanded.toString());
    });

    requestAnimationFrame(() => {
      const focusTarget = panel.querySelector('.menu-drawer__back-button, .menu-drawer__menu-item');
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    });
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        this.#resetPanels();
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
